import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  User, Save, Loader2, ExternalLink, Shield,
  Camera, Globe, X, Plus
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { PublishedWorksCard } from '../components/profile/PublishedWorksCard';
import { ChangePasswordCard } from '../components/profile/ChangePasswordCard';

const GENRE_OPTIONS = [
  "General Fiction", "Literary Fiction", "Children's", "Poetry", "Romance",
  "Fantasy", "Science Fiction", "Horror", "Historical Fiction", "Mystery/Thriller",
  "Creative Non-Fiction", "Memoir", "Young Adult", "Flash Fiction", "Short Story",
  "Novella", "Biography"
];

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, updateProfile, api } = useAuth();
  const [formData, setFormData] = useState({
    display_name: user?.display_name || '',
    bio: user?.bio || '',
    avatar_url: user?.avatar_url || '',
    favorite_genres: user?.favorite_genres || [],
    currently_working_on: user?.currently_working_on || '',
    website_url: user?.website_url || '',
    social_links: user?.social_links || {},
    how_i_got_here: user?.how_i_got_here || '',
    what_ive_done: user?.what_ive_done || '',
    additional_bio: user?.additional_bio || ''
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [socialKey, setSocialKey] = useState('');
  const [socialValue, setSocialValue] = useState('');
  const [publishedWorks, setPublishedWorks] = useState([]);

  useEffect(() => {
    const fetchWorks = async () => {
      if (!user) return;
      try {
        const res = await api.get(`/users/${user.id}/published-works`);
        setPublishedWorks(res.data);
      } catch (e) { /* e logged */ }
    };
    fetchWorks();
  }, [user, api]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      await updateProfile(formData);
      setMessage('Profile updated successfully!');
    } catch (error) {
      
      setMessage('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      setMessage('Photo must be under 5MB');
      return;
    }
    
    setUploading(true);
    setMessage('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/upload/avatar', fd);
      setFormData(prev => ({ ...prev, avatar_url: res.data.avatar_url }));
      setMessage('Photo uploaded! Click Save to keep changes.');
    } catch (error) {
      
      setMessage('Failed to upload photo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const toggleGenre = (genre) => {
    setFormData(prev => ({
      ...prev,
      favorite_genres: prev.favorite_genres.includes(genre)
        ? prev.favorite_genres.filter(g => g !== genre)
        : [...prev.favorite_genres, genre]
    }));
  };

  const addSocialLink = () => {
    if (socialKey.trim() && socialValue.trim()) {
      setFormData(prev => ({
        ...prev,
        social_links: { ...prev.social_links, [socialKey.trim()]: socialValue.trim() }
      }));
      setSocialKey('');
      setSocialValue('');
    }
  };

  const removeSocialLink = (key) => {
    setFormData(prev => {
      const updated = { ...prev.social_links };
      delete updated[key];
      return { ...prev, social_links: updated };
    });
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  const avatarSrc = formData.avatar_url?.startsWith('/api')
    ? `${API_URL}${formData.avatar_url}`
    : formData.avatar_url;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="profile-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-3xl font-semibold text-text-primary">Profile Settings</h1>
        <Link to={`/writer/${user.id}`} data-testid="view-portfolio-link">
          <Button variant="outline" className="btn-outline gap-2">
            <ExternalLink className="w-4 h-4" />
            View My Portfolio
          </Button>
        </Link>
      </div>

      <Card className="border-brand-primary/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-brand-accent" />
            Your Profile
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-6">
            {/* Avatar with Upload */}
            <div className="flex items-center gap-6">
              <div className="relative group">
                <Avatar className="w-20 h-20 border-2 border-brand-primary/10">
                  <AvatarImage src={avatarSrc} alt={formData.display_name} />
                  <AvatarFallback className="bg-brand-primary text-white text-2xl">
                    {formData.display_name?.[0]?.toUpperCase() || user.username?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <label 
                  className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  data-testid="avatar-upload-trigger"
                >
                  {uploading ? (
                    <Loader2 className="w-5 h-5 text-white animate-spin" />
                  ) : (
                    <Camera className="w-5 h-5 text-white" />
                  )}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    disabled={uploading}
                    data-testid="avatar-file-input"
                  />
                </label>
              </div>
              <div>
                <p className="font-medium text-text-primary">@{user.username}</p>
                <p className="text-sm text-text-muted">{user.email}</p>
                <label className="inline-flex items-center gap-1.5 mt-2 text-sm text-brand-accent hover:text-brand-primary cursor-pointer transition-colors" data-testid="avatar-upload-button">
                  {uploading ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Camera className="w-3.5 h-3.5" />
                  )}
                  {uploading ? 'Uploading...' : 'Upload Photo'}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
                <p className="text-xs text-text-muted">JPEG, PNG, GIF, WebP — max 5MB</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="display_name">Display Name</Label>
              <Input
                id="display_name"
                placeholder="Your display name"
                value={formData.display_name}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                data-testid="display-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio">Bio</Label>
              <Textarea
                id="bio"
                placeholder="Tell us about yourself and your writing..."
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                rows={4}
                data-testid="bio-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="currently_working_on">Currently Working On</Label>
              <Input
                id="currently_working_on"
                placeholder="e.g., A mystery novel set in 1920s Paris"
                value={formData.currently_working_on}
                onChange={(e) => setFormData({ ...formData, currently_working_on: e.target.value })}
                data-testid="currently-working-on-input"
              />
            </div>

            {/* Writing Journey Bio Sections */}
            <div className="pt-4 border-t border-[#E5E2DC]">
              <p className="text-xs font-medium text-text-muted uppercase tracking-wider mb-4">Your Writing Story</p>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="how_i_got_here">How I Got Here</Label>
                  <Textarea
                    id="how_i_got_here"
                    placeholder="Your writing journey — what brought you to the craft, what inspires you..."
                    value={formData.how_i_got_here}
                    onChange={(e) => setFormData({ ...formData, how_i_got_here: e.target.value })}
                    rows={4}
                    data-testid="how-i-got-here-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="what_ive_done">What I've Done</Label>
                  <Textarea
                    id="what_ive_done"
                    placeholder="Published works, accomplishments, awards, writing credits..."
                    value={formData.what_ive_done}
                    onChange={(e) => setFormData({ ...formData, what_ive_done: e.target.value })}
                    rows={4}
                    data-testid="what-ive-done-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="additional_bio">Anything Else You'd Like to Share</Label>
                  <Textarea
                    id="additional_bio"
                    placeholder="Fun facts, guilty pleasures, favorite authors, coffee order... writers love to overshare!"
                    value={formData.additional_bio}
                    onChange={(e) => setFormData({ ...formData, additional_bio: e.target.value })}
                    rows={3}
                    data-testid="additional-bio-input"
                  />
                </div>
              </div>
            </div>

            {/* Favorite Genres */}
            <div className="space-y-2">
              <Label>Favorite Genres</Label>
              <div className="flex flex-wrap gap-2" data-testid="genre-selector">
                {GENRE_OPTIONS.map(genre => (
                  <button
                    key={genre}
                    type="button"
                    onClick={() => toggleGenre(genre)}
                    className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
                      formData.favorite_genres.includes(genre)
                        ? 'bg-brand-primary text-white border-brand-primary'
                        : 'bg-white text-text-secondary border-brand-primary/20 hover:border-brand-primary/50'
                    }`}
                  >
                    {genre}
                  </button>
                ))}
              </div>
            </div>

            {/* Website */}
            <div className="space-y-2">
              <Label htmlFor="website_url" className="flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5" />
                Website / Blog
              </Label>
              <Input
                id="website_url"
                placeholder="https://yourwebsite.com"
                value={formData.website_url}
                onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                data-testid="website-url-input"
              />
            </div>

            {/* Social Links */}
            <div className="space-y-2">
              <Label>Social Links</Label>
              {Object.entries(formData.social_links).length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {Object.entries(formData.social_links).map(([key, value]) => (
                    <span 
                      key={key}
                      className="flex items-center gap-1.5 px-3 py-1 bg-brand-primary/10 text-brand-primary rounded-full text-sm"
                    >
                      {key}: {value.length > 25 ? value.substring(0, 25) + '...' : value}
                      <button type="button" onClick={() => removeSocialLink(key)} className="hover:text-red-500">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-2" data-testid="social-link-adder">
                <Input
                  placeholder="Platform (e.g., Twitter)"
                  value={socialKey}
                  onChange={(e) => setSocialKey(e.target.value)}
                  className="flex-1"
                />
                <Input
                  placeholder="URL or handle"
                  value={socialValue}
                  onChange={(e) => setSocialValue(e.target.value)}
                  className="flex-1"
                />
                <Button type="button" variant="outline" size="icon" onClick={addSocialLink} data-testid="add-social-link-btn">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {message && (
              <div className={`p-3 rounded-lg text-sm ${
                message.includes('success') || message.includes('uploaded')
                  ? 'bg-green-50 text-green-600' 
                  : 'bg-red-50 text-red-600'
              }`} data-testid="profile-message">
                {message}
              </div>
            )}

            <div className="flex justify-end">
              <Button 
                type="submit" 
                disabled={saving}
                className="btn-primary gap-2"
                data-testid="save-profile-button"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Changes
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <PublishedWorksCard publishedWorks={publishedWorks} setPublishedWorks={setPublishedWorks} api={api} />

      <ChangePasswordCard api={api} />

      {/* Ownership & Originality Notice */}
      <div className="mt-6 p-5 bg-surface-alt rounded-xl border border-brand-primary/10" data-testid="profile-ownership-notice">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-brand-primary flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-text-secondary">
              <strong className="text-text-primary">You own 100% of your work.</strong> Collectively Write claims no rights to any content you create. 
              We also require all submissions to be original writing—no AI-generated content.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
